import {
	Permission,
	Transaction,
	TransactionType,
	useUserPermissionGrantsQuery,
} from '../../../graphql'

export type CancelTransactionPermission = {
	/**
	 * False when the sending-warehouse rule blocks this viewer, and while the
	 * answer to that is still loading.
	 */
	canCancel: boolean,
	/**
	 * The rule is what blocks this viewer: they hold the cancel grant for the
	 * receiving store only. The only case that gets the explanatory tooltip —
	 * a button disabled because permissions are still loading gets none.
	 */
	blockedAsReceiver: boolean,
}

export type CancellableTransaction = Pick<Transaction, 'transactionType' | 'fromId' | 'toId' | 'from'>

/** `from` is a union; only the store arms carry `isWarehouse`, never a Provider. */
function isWarehouseSource( from: Transaction['from'] ): boolean {
	return !!from && 'isWarehouse' in from && !!from.isWarehouse
}

/**
 * Whether this user may cancel a TX-#'d (transaction-based) transfer.
 *
 * `StoreToStoreStatus.handle_migrate_to_cancelled` in inventory-api only allows
 * the sending side to cancel when the sending location is a warehouse: the
 * receiving store has not taken ownership of the inventory, so its lever is
 * receiving (or declining to receive), not cancelling. Store-to-store transfers
 * between ordinary stores keep the legacy behaviour where either party may
 * cancel, and other transaction types are untouched by that rule.
 *
 * This mirrors that check so the Cancel action can be disabled with an
 * explanation, instead of the user submitting and getting back the generic
 * "You do not have permission to do that." toast that the 403 branch of
 * `graphql/error-handlers` produces.
 *
 * `isWarehouse` rides along on the row via `DetailedTransaction`, so deciding
 * whether the rule applies costs nothing. Only the permission lookup is a
 * query, and it is skipped unless the rule actually applies. While it is in
 * flight the action is disabled: enabling it would let a receiving-side viewer
 * click through to the generic 403 toast this hook exists to prevent. Because
 * the lookup only happens for warehouse-sourced store-to-store transfers, every
 * other transaction's Cancel stays enabled with no wait.
 *
 * Both store ids go into that one lookup so the receiving-side viewer can be
 * told apart from someone holding no grant at all — only the former gets the
 * disabled button, since only they are who the rule redirects.
 *
 * This is safe to call per row: `ProcessingStatusActions` mounts inside an
 * `EuiPopover` panel, which EUI does not render until the popover opens, so the
 * query fires once per opened row rather than once per row in the grid.
 */
export default function useCanCancelTransaction( transaction: CancellableTransaction ): CancelTransactionPermission {
	const { transactionType, fromId, toId, from } = transaction

	const ruleApplies = transactionType === TransactionType.StoreToStore &&
		!!fromId &&
		isWarehouseSource( from )

	const { data, loading } = useUserPermissionGrantsQuery( {
		variables: {
			permissions: [ Permission.CanCancelPartsTransferByStoreId ],
			objectIds: [ fromId, toId ].filter( ( id ) => !!id ).map( String ),
		},
		skip: !ruleApplies,
	} )

	if ( !ruleApplies ) {
		return { canCancel: true, blockedAsReceiver: false }
	}

	if ( loading ) {
		return { canCancel: false, blockedAsReceiver: false }
	}

	const granted = new Set(
		( data?.userPermissionGrants ?? [] )
			.filter( ( grant ) => grant.permission === Permission.CanCancelPartsTransferByStoreId )
			.flatMap( ( grant ) => grant.objectIds.map( ( objectId ) => String( objectId ) ) )
	)

	const holdsSenderGrant = granted.has( String( fromId ) )
	const holdsReceiverGrant = !!toId && granted.has( String( toId ) )

	// Block only the viewer the rule is actually about: one whose grant is on the
	// receiving store. A viewer with no grant on either store is left to the API,
	// exactly as before this rule existed — it rejects them either way, and the
	// Centralized Purchasing copy would not explain why to someone who never had
	// the permission.
	const blockedAsReceiver = !holdsSenderGrant && holdsReceiverGrant

	return { canCancel: !blockedAsReceiver, blockedAsReceiver }
}
